import React, { useEffect, useState } from 'react';
import { Box, TextField, FormControlLabel, Checkbox, Typography } from '@mui/material';
import { Theme } from '@mui/material/styles/createTheme';
import makeStyles from '@material-ui/core/styles/makeStyles';
import createStyles from '@material-ui/core/styles/createStyles';
import { styled } from '@mui/material/styles';
import { FlatButton } from '../Buttons/FlatButton';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { _selectedTopicId, _topics, updateTopic, changeFABLogo } from '../../store/slices/topicSlice';
import { _activeLanguageCode } from '../../store/slices/editorSlice';
import { bindActionCreators } from 'redux';
import { _selectedLocation } from '../../store/slices/locationSlice';
import { hashPassword, validatePassword } from '../../util/passwordUtils';
import { DroppableImage } from '../Editable/DroppableImage';
import { IMAGE_PREVIEW } from '../../util/constants';
import { QRColorPickerPopover } from '../Dashboard/QREditor/QRColorPickerPopover';

interface PageMetadataProps {
	organizationName: string;
	pageTitle: string;
	description: string;
	head?: string;
	lat?: string;
	lng?: string;
	password?: string;
	isPasswordProtected?: boolean;
	pageSlug?: string;
	redirectUrl?: string;
	qrBackgroundColor?: string;
	fabAccentColor?: string;
}

const Form = styled('form')(() => ({
	display: 'flex',
	flexDirection: 'column',
	gap: '24px',
	width: '100%',
	height: '100%',
}));

const StyledTextField = styled(TextField)(() => ({
	'& .MuiOutlinedInput-root': {
		backgroundColor: '#F8F9FA',
		borderRadius: '8px',
		'& fieldset': {
			borderColor: '#E9ECEF',
		},
		'&:hover fieldset': {
			borderColor: '#CED4DA',
		},
		'&.Mui-focused fieldset': {
			borderColor: '#1976d2',
		},
	},
	'& .MuiInputLabel-root': {
		color: '#495057',
		fontWeight: 500,
		'&.Mui-focused': {
			color: '#1976d2',
		},
	},
	'& .MuiInputBase-input': {
		padding: '14px 16px',
	},
	'& .MuiInputBase-multiline': {
		padding: '0',
	},
}));

const customStyles = makeStyles((theme: Theme) =>
	createStyles({
		wrapper: {
			// height: '100%',
			display: 'flex',
			flexDirection: 'column',
		},
		content: {
			flex: 1,
			display: 'flex',
			flexDirection: 'column',
			gap: '24px',
			padding: '0px 24px',
			overflowY: 'auto',
		},
		formFields: {
			display: 'flex',
			flexDirection: 'column',
			marginTop: '5px',
			gap: '24px',
		},
		buttonBox: {
			display: 'flex',
			justifyContent: 'flex-end',
			padding: '16px 24px',
			// borderTop: '1px solid #E9ECEF',
			backgroundColor: '#ffffff',
		},
		button: {
			height: 40,
			borderRadius: '8px',
			textTransform: 'none',
			padding: '8px 24px',
			fontWeight: 500,
		},
		helperText: {
			marginTop: '4px',
			color: '#6C757D',
			fontSize: '12px',
		},
	})
);

const PageMetadata: React.FC = () => {
	const topics = useAppSelector(_topics);
	const topicId = useAppSelector(_selectedTopicId);
	const activeLanguageCode = useAppSelector(_activeLanguageCode);
	const classes = customStyles();
	const dispatch = useAppDispatch();
	const updateMetadata = bindActionCreators(updateTopic, dispatch);
	const changeFABLogoAction = bindActionCreators(changeFABLogo, dispatch);
	const [enableButton, setEnableButton] = useState(false);
	const selectedLocation = useAppSelector(_selectedLocation);
	const [fabLogoPreview, setFabLogoPreview] = useState(() => {
		const fabLogoUrl = topics[topicId]?.metadata?.fabLogoUrl;
		// Only use non-blob URLs for initial state
		return fabLogoUrl && !fabLogoUrl.startsWith('blob:') ? fabLogoUrl : '';
	});

	const [formData, setFormData] = React.useState<PageMetadataProps>({
		...topics[topicId]?.metadata,
		organizationName: topics[topicId]?.metadata?.organizationName || selectedLocation?.name,
		pageTitle:
			topics[topicId]?.metadata?.pageTitle ||
			topics[topicId]?.name.find((n) => n.language === activeLanguageCode)?.name,
		head: topics[topicId]?.metadata?.head || '',
		// Never pre-fill the password from Redux — it contains the bcrypt hash, not plaintext.
		// The field starts empty; users re-enter the password only when they want to change it.
		password: '',
		isPasswordProtected: topics[topicId]?.metadata?.isPasswordProtected || false,
		pageSlug: topics[topicId]?.metadata?.pageSlug || '',
		redirectUrl: topics[topicId]?.metadata?.redirectUrl || '',
		qrBackgroundColor: topics[topicId]?.metadata?.qrBackgroundColor || '',
		fabAccentColor: topics[topicId]?.metadata?.fabAccentColor || '',
	});
	const [error, setError] = useState('');

	const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const { name, value } = e.target;
		setFormData((prev) => ({
			...prev,
			[name]: value,
		}));
		setError('');

		// Enable button only if any field has changed from initial values
		const hasChanges = value !== topics[topicId]?.metadata?.[name];
		setEnableButton(hasChanges);
	};

	const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
		// TOD: verify link name validity by replacing special characters and spaces with hyphens
		const pageSlug = e.target.value.replace(/[^a-zA-Z0-9-]/g, '-');
		if (pageSlug !== e.target.value) {
			setFormData((prev) => ({
				...prev,
				pageSlug: pageSlug,
			}));
		}
	};

	// Saving metadata to the store then save the topic to mongo
	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		setError('');

		// Validate password if password protection is being enabled or password is being changed
		const alreadyProtected = topics[topicId]?.metadata?.isPasswordProtected;
		if (formData.isPasswordProtected) {
			if (!formData.password && !alreadyProtected) {
				// First time enabling — password is required
				setError('Password is required when password protection is enabled');
				return;
			}
			if (formData.password && !validatePassword(formData.password)) {
				setError('Password must be at least 4 characters long');
				return;
			}
		}

		let clonedTopic = structuredClone(topics[topicId]);

		// Hash the password before saving if password protection is enabled
		const metadataToSave = { ...formData };
		if (formData.isPasswordProtected && formData.password) {
			// User entered a new password — hash and send it
			metadataToSave.password = await hashPassword(formData.password);
		} else if (formData.isPasswordProtected && !formData.password) {
			// Password field is empty but protection is on — don't send password field
			// so the server keeps the existing hash in the database
			delete metadataToSave.password;
		} else if (!formData.isPasswordProtected) {
			// Clear password if password protection is disabled
			metadataToSave.password = '';
		}

		// Remember the plaintext password so we can keep it in the form after save
		const plaintextPassword = formData.password;

		// Preserve fabLogoUrl from the topic if it exists and is not a blob URL
		// (blob URLs should not be saved - they're temporary)
		const existingFabLogoUrl = clonedTopic.metadata?.fabLogoUrl;
		if (existingFabLogoUrl && !existingFabLogoUrl.startsWith('blob:')) {
			(metadataToSave as any).fabLogoUrl = existingFabLogoUrl;
		} else if (existingFabLogoUrl && existingFabLogoUrl.startsWith('blob:')) {
			// If there's a blob URL, don't save it (upload might still be in progress)
			// Remove it from metadata to prevent saving blob URLs
			delete (metadataToSave as any).fabLogoUrl;
		}

		clonedTopic.metadata = metadataToSave;
		// Saving metadata - the middleware returns a promise that we can await
		try {
			await updateMetadata(clonedTopic);
			// Disable the save button after successful save
			setEnableButton(false);
			// Keep the plaintext password in the form so the user sees what they typed,
			// not the hash that comes back from Redux. The server no longer returns the
			// hash, but Redux may still hold it from the optimistic update.
			if (formData.isPasswordProtected && plaintextPassword) {
				setFormData((prev) => ({ ...prev, password: plaintextPassword }));
			}
		} catch (error: any) {
			// Error is already handled by axios interceptor and shown via toast
			// Extract and show the actual error message in the form
			let errorMessage = 'An error occurred while saving. Please try again.';

			if (error?.response?.data) {
				const responseData = error.response.data;
				if (typeof responseData === 'string') {
					// Extract the error message before the pipe character
					const messageParts = responseData.split(' | ');
					if (messageParts.length > 0 && messageParts[0].trim()) {
						errorMessage = messageParts[0].trim();
					}
				} else if (typeof responseData === 'object' && responseData.message) {
					errorMessage = responseData.message;
				}
			} else if (error?.message) {
				errorMessage = error.message;
			}

			setError(errorMessage);
		}
	};

	useEffect(() => {
		if (activeLanguageCode && topicId) {
			setEnableButton(false);
			const selectedTopic = topics[topicId];
			const pageName =
				selectedTopic?.metadata?.pageTitle ||
				selectedTopic?.name.find((n) => n.language === activeLanguageCode)?.name;
			setFormData((prev) => ({
				...prev,
				organizationName: selectedTopic?.metadata?.organizationName || selectedLocation?.name,
				pageTitle: pageName,
				lat: selectedTopic?.metadata?.lat || '',
				lng: selectedTopic?.metadata?.lng || '',
				description: selectedTopic?.metadata?.description || '',
				pageSlug: selectedTopic?.metadata?.pageSlug || '',
				redirectUrl: selectedTopic?.metadata?.redirectUrl || '',
				head: selectedTopic?.metadata?.head || '',
				// Never load the password hash from Redux into the form — it would get
				// re-hashed on next save. Always start with an empty password field.
				// The user must re-enter the password if they want to change it.
				password: '',
				isPasswordProtected: selectedTopic?.metadata?.isPasswordProtected || false,
				qrBackgroundColor: selectedTopic?.metadata?.qrBackgroundColor || '',
				fabAccentColor: selectedTopic?.metadata?.fabAccentColor || '',
			}));
			// Update preview when metadata.fabLogoUrl changes (but ignore blob URLs)
			const fabLogoUrl = selectedTopic?.metadata?.fabLogoUrl;
			if (fabLogoUrl && !fabLogoUrl.startsWith('blob:')) {
				setFabLogoPreview(fabLogoUrl);
			} else if (!fabLogoUrl) {
				setFabLogoPreview('');
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [activeLanguageCode, topicId, selectedLocation]);

	// Update preview when fabLogoUrl changes in Redux (after upload completes)
	const fabLogoUrl = topics[topicId]?.metadata?.fabLogoUrl;
	useEffect(() => {
		const selectedTopic = topics[topicId];
		const currentFabLogoUrl = selectedTopic?.metadata?.fabLogoUrl;
		if (currentFabLogoUrl) {
			// Only update if it's a server URL (not a blob URL)
			if (!currentFabLogoUrl.startsWith('blob:')) {
				setFabLogoPreview(currentFabLogoUrl);
			}
		}
	}, [fabLogoUrl, topicId, topics]);

	const orgIdInputLongLat = selectedLocation?.organizationId === process.env.REACT_APP_ORG_ID_EXTRAS;
	return (
		<Box className={classes.wrapper}>
			<Form onSubmit={handleSubmit}>
				<Box className={classes.content}>
					<Box className={classes.formFields}>
						<StyledTextField
							fullWidth
							label="Organization Name"
							name="organizationName"
							value={formData.organizationName}
							onChange={handleChange}
							placeholder="Enter your organization name"
							InputLabelProps={{ shrink: true }}
						/>

						<StyledTextField
							fullWidth
							label="Page Title"
							name="pageTitle"
							value={formData.pageTitle}
							onChange={handleChange}
							placeholder="Enter your page title"
							InputLabelProps={{ shrink: true }}
						/>

						<StyledTextField
							fullWidth
							label="Page Slug"
							name="pageSlug"
							value={formData.pageSlug}
							onChange={handleChange}
							onBlur={handleBlur}
							error={!!error}
							placeholder="Enter your page slug"
							InputLabelProps={{ shrink: true }}
						/>
						{error && (
							<Box>
								<Typography
									style={{
										textAlign: 'center',
										whiteSpace: 'pre-line',
										wordBreak: 'break-word',
										overflowWrap: 'break-word',
									}}
									color="error"
								>
									{error}
								</Typography>
							</Box>
						)}

						<StyledTextField
							fullWidth
							label="Redirect URL (when hidden)"
							name="redirectUrl"
							value={formData.redirectUrl}
							onChange={handleChange}
							placeholder="Exhibit ID, page slug, appSlug/pageSlug, or https:// URL"
							InputLabelProps={{ shrink: true }}
							FormHelperTextProps={{
								sx: {
									whiteSpace: 'pre-line',
									wordBreak: 'break-word',
									overflowWrap: 'break-word',
								},
							}}
							helperText="When this page is hidden, visitors are sent here instead of the default first active page."
						/>

						{orgIdInputLongLat && (
							<>
								<StyledTextField
									fullWidth
									label="Latitude"
									name="lat"
									value={formData.lat}
									onChange={handleChange}
									placeholder="Enter your latitude"
									InputLabelProps={{ shrink: true }}
								/>

								<StyledTextField
									fullWidth
									label="Longitude"
									name="lng"
									value={formData.lng}
									onChange={handleChange}
									placeholder="Enter your longitude"
									InputLabelProps={{ shrink: true }}
								/>
							</>
						)}
						<StyledTextField
							fullWidth
							label="Page Description"
							name="description"
							value={formData.description}
							onChange={handleChange}
							multiline
							rows={4}
							placeholder="This description will appear when a link is shared on social media platforms like Facebook, LinkedIn, and X."
							InputLabelProps={{ shrink: true }}
						/>

						<StyledTextField
							fullWidth
							label="Head Code"
							name="head"
							multiline
							rows={4}
							value={formData.head}
							onChange={handleChange}
							placeholder="Enter head related code that will appear in the page head tag"
							InputLabelProps={{ shrink: true }}
						/>

						<Box sx={{ mt: 2, mb: 1 }}>
							<FormControlLabel
								control={
									<Checkbox
										checked={formData.isPasswordProtected}
										onChange={(e) => {
											setFormData((prev) => ({
												...prev,
												isPasswordProtected: e.target.checked,
											}));
											setEnableButton(true);
										}}
										name="isPasswordProtected"
									/>
								}
								label="Password protect this page"
							/>
						</Box>

						{formData.isPasswordProtected && (
							<Box>
								<StyledTextField
									fullWidth
									label="Page Password"
									name="password"
									type="password"
									value={formData.password}
									onChange={handleChange}
									placeholder={
										topics[topicId]?.metadata?.isPasswordProtected
											? '••••••••••'
											: 'Enter a password to protect this page'
									}
									InputLabelProps={{ shrink: true }}
									FormHelperTextProps={{
										sx: {
											whiteSpace: 'pre-line',
											wordBreak: 'break-word',
											overflowWrap: 'break-word',
											textAlign: 'center',
										},
									}}
									helperText={
										topics[topicId]?.metadata?.isPasswordProtected && !formData.password
											? 'Leave blank to keep current password, or type a new one'
											: 'Anyone accessing this page will need to enter this password'
									}
								/>
							</Box>
						)}

						<Box sx={{ mt: 2 }}>
							<Typography variant="body2" sx={{ mb: 1, fontWeight: 500, color: '#495057' }}>
								FAB Logo
							</Typography>
							<Typography
								variant="caption"
								sx={{ mb: 2, color: '#6C757D', display: 'block' }}
								title="Upload custom image for FAB"
							>
								Upload custom image for FAB
							</Typography>
							<Box sx={{ width: '100%', cursor: 'pointer' }}>
								<DroppableImage
									imageUrl={fabLogoPreview}
									setImagePreview={(file: File) => {
										const url = URL.createObjectURL(file);
										setFabLogoPreview(url);
										changeFABLogoAction({ name: file.name, url });
									}}
									imageType={IMAGE_PREVIEW}
								/>
							</Box>
						</Box>

						<Box sx={{ mt: 2 }}>
							<Typography variant="body2" sx={{ mb: 1, fontWeight: 500, color: '#495057' }}>
								QR Background Color
							</Typography>
							<Typography variant="caption" sx={{ mb: 2, color: '#6C757D', display: 'block' }}>
								Background color behind the QR code in the share drawer. Leave empty for default.
							</Typography>
							<QRColorPickerPopover
								name="qrBackgroundColor"
								label="QR Background"
								color={formData.qrBackgroundColor || '#FFFFFF'}
								setColor={(color: string) => {
									setFormData((prev) => ({ ...prev, qrBackgroundColor: color }));
									setEnableButton(true);
								}}
								setError={() => {}}
							/>
						</Box>

						<Box sx={{ mt: 2 }}>
							<Typography variant="body2" sx={{ mb: 1, fontWeight: 500, color: '#495057' }}>
								FAB Accent Color
							</Typography>
							<Typography variant="caption" sx={{ mb: 2, color: '#6C757D', display: 'block' }}>
								Override the button and icon color in the share drawer. Leave empty to use QR eye color.
							</Typography>
							<QRColorPickerPopover
								name="fabAccentColor"
								label="FAB Accent"
								color={formData.fabAccentColor || '#000000'}
								setColor={(color: string) => {
									setFormData((prev) => ({ ...prev, fabAccentColor: color }));
									setEnableButton(true);
								}}
								setError={() => {}}
							/>
						</Box>
					</Box>
				</Box>

				<Box className={classes.buttonBox}>
					<FlatButton
						disabled={!enableButton}
						type="submit"
						className={classes.button}
						variant="contained"
						color="primary"
					>
						Save
					</FlatButton>
				</Box>
			</Form>
		</Box>
	);
};

export default PageMetadata;
